//src/lib/permissions.ts
export const PERMISSIONS = {
  // Normex / Platform
  PLATFORM_TENANTS_READ: "platform.tenants.read",
  PLATFORM_TENANTS_WRITE: "platform.tenants.write",
  PLATFORM_TENANTS_BOOTSTRAP: "platform.tenants.bootstrap",
  PLATFORM_SMTP_MANAGE: "platform.smtp.manage",
  PLATFORM_AI_CREDITS_MANAGE: "platform.ai_credits.manage",
  PLATFORM_PERMISSIONS_MANAGE: "platform.permissions.manage",
  PLATFORM_FEATURES_READ: "platform.features.read",
  PLATFORM_FEATURES_WRITE: "platform.features.write",
  PLATFORM_CONTEXT_ADMIN: "platform.context.admin",

  // Portal da Empresa / Tenant
  TENANT_DASHBOARD_READ: "tenant.dashboard.read",
  TENANT_CLIENTS_READ: "tenant.clients.read",
  TENANT_CLIENTS_WRITE: "tenant.clients.write",
  TENANT_USERS_READ: "tenant.users.read",
  TENANT_USERS_WRITE: "tenant.users.write",
  TENANT_GROUPS_READ: "tenant.groups.read",
  TENANT_GROUPS_WRITE: "tenant.groups.write",
  TENANT_PERMISSIONS_READ: "tenant.permissions.read",
  TENANT_MODELS_DEPARTMENTS_READ: "tenant.models.departments.read",
  TENANT_MODELS_DEPARTMENTS_WRITE: "tenant.models.departments.write",
  TENANT_MODELS_JOB_POSITIONS_READ: "tenant.models.job_positions.read",
  TENANT_MODELS_JOB_POSITIONS_WRITE: "tenant.models.job_positions.write",
  TENANT_MODELS_QUESTIONNAIRES_READ: "tenant.models.questionnaires.read",
  TENANT_MODELS_QUESTIONNAIRES_WRITE: "tenant.models.questionnaires.write",
  TENANT_MODELS_GRO_READ: "tenant.models.gro.read",
  TENANT_MODELS_GRO_WRITE: "tenant.models.gro.write",
  TENANT_CREDITS_READ: "tenant.credits.read",
  TENANT_CREDITS_WRITE: "tenant.credits.write",
  TENANT_CONSUMPTION_READ: "tenant.consumption.read",
  TENANT_BILLING_READ: "tenant.billing.read",
  TENANT_BILLING_WRITE: "tenant.billing.write",
  TENANT_SETTINGS_READ: "tenant.settings.read",
  TENANT_SETTINGS_WRITE: "tenant.settings.write",
  TENANT_AUDIT_READ: "tenant.audit.read",

  // Operação do Cliente. Os aliases tradicionais abaixo permanecem para reduzir
  // alterações nas telas operacionais já validadas.
  DASHBOARD_READ: "client.dashboard.read",
  PEOPLE_READ: "client.people.read",
  PEOPLE_WRITE: "client.people.write",
  USERS_READ: "client.users.read",
  USERS_WRITE: "client.users.write",
  GROUPS_READ: "tenant.groups.read",
  GROUPS_WRITE: "tenant.groups.write",
  PERMISSIONS_READ: "tenant.permissions.read",
  TENANT_READ: "tenant.settings.read",
  TENANT_WRITE: "tenant.settings.write",
  CLIENT_COMPANIES_READ: "tenant.clients.read",
  CLIENT_COMPANIES_WRITE: "tenant.clients.write",
  UNITS_READ: "client.units.read",
  UNITS_WRITE: "client.units.write",
  DEPARTMENTS_READ: "client.departments.read",
  DEPARTMENTS_WRITE: "client.departments.write",
  JOB_POSITIONS_READ: "client.job_positions.read",
  JOB_POSITIONS_WRITE: "client.job_positions.write",
  RISKS_READ: "client.risks.read",
  RISKS_WRITE: "client.risks.write",
  AREAS_READ: "client.areas.read",
  AREAS_WRITE: "client.areas.write",
  INSPECTIONS_READ: "client.inspections.read",
  INSPECTIONS_WRITE: "client.inspections.write",
  ACTIONS_READ: "client.action_plans.read",
  ACTIONS_WRITE: "client.action_plans.write",
  DOCUMENTS_READ: "client.documents.read",
  DOCUMENTS_WRITE: "client.documents.write",
  TRAININGS_READ: "client.trainings.read",
  TRAININGS_WRITE: "client.trainings.write",
  REPORTS_READ: "client.reports.read",
  REPORTS_EXPORT: "client.reports.export",
  NOTIFICATIONS_READ: "client.notifications.read",
  NOTIFICATIONS_WRITE: "client.notifications.write",
  AUDIT_READ: "client.audit.read",
  GRO_SETTINGS_READ: "client.gro.settings.read",
  GRO_SETTINGS_WRITE: "client.gro.settings.write",
  DIMENSIONS_READ: "client.dimensions.read",
  DIMENSIONS_WRITE: "client.dimensions.write",
  DIMENSION_RISK_RULES_READ: "client.dimension_risk_rules.read",
  DIMENSION_RISK_RULES_WRITE: "client.dimension_risk_rules.write",
  WORKER_GROUPS_READ: "client.worker_groups.read",
  WORKER_GROUPS_WRITE: "client.worker_groups.write",
  QUESTIONNAIRES_READ: "client.questionnaires.read",
  QUESTIONNAIRES_WRITE: "client.questionnaires.write",
  SURVEY_CAMPAIGNS_READ: "client.survey_campaigns.read",
  SURVEY_CAMPAIGNS_WRITE: "client.survey_campaigns.write",
  SURVEY_CAMPAIGNS_REOPEN: "client.survey_campaigns.reopen",
  SURVEY_RESULTS_READ: "client.survey_results.read",
  SURVEY_RESPONSES_REIDENTIFY: "client.survey_responses.reidentify",
  RISK_INVENTORY_READ: "client.risk_inventory.read",
  RISK_INVENTORY_WRITE: "client.risk_inventory.write",
  ACTION_PLANS_READ: "client.action_plans.read",
  ACTION_PLANS_WRITE: "client.action_plans.write",
} as const;

export type PermissionCode = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];
export const ALL_PERMISSIONS: PermissionCode[] = Array.from(new Set(Object.values(PERMISSIONS)));
export const ADMIN_PERMISSIONS: PermissionCode[] = ALL_PERMISSIONS;

export type PermissionLikeUser = {
  access_scope?: "platform" | "tenant" | string | null;
  context_type?: "platform" | "tenant" | "client" | string | null;
  token_context_type?: "platform" | "tenant" | "client" | string | null;
  permissions?: string[] | null;
} | null | undefined;

export function hasAnyPermission(user: PermissionLikeUser, permissions: readonly string[]) {
  const userPermissions = user?.permissions ?? [];
  return permissions.some((permission) => userPermissions.includes(permission));
}

export function isTenantAdminUser(user: PermissionLikeUser) {
  if (!user) return false;
  if (user.access_scope === "platform" || user.context_type === "platform") return true;

  // Todo usuário que entrou pelo contexto da Empresa continua sendo usuário da
  // Empresa, independentemente de possuir permissões administrativas de escrita.
  // A carteira de Clientes apenas habilita a entrada no contexto operacional do Cliente.
  if (user.token_context_type === "tenant") return true;

  // Compatibilidade com sessões antigas que ainda não retornam token_context_type.
  return user.access_scope === "tenant" && user.context_type === "tenant";
}
